import { useSyncExternalStore } from 'react';
import type { AnyRouter } from '@tanstack/react-router';

import { safeRedirect } from '@/features/auth/session';

/*
  Where Back goes (ALS-105, from the prototype's `C.backTarget` in scripts/components.js): to the
  page you came from. A record opened from a client's Wallet & orders returns to that client, on
  that tab, rather than to the register its screen happens to be drawn against.

  The rule lives here and nowhere else. A subpage header hands over the screen's own default and
  gets the computed target back, so no screen decides its own way back.

  Three things it has to get right, all of them the prototype's:

  - the way back is remembered per record for the browser session, so a reload, or a save that
    re-enters the page, keeps it rather than falling back to the default
  - COMING BACK IS NOT COMING FROM: returning to a client from a record whose own way back was
    that client leaves the client's Back pointing at the register, not at the record
  - the screen's default is what a direct link or a bookmark gets, because there is nothing to
    remember

  What makes a page identifiable here is its pathname alone. Ids are path segments
  (`/customers/65`), so one key per pathname is already one key per record; the prototype needed
  `?id=` in its key because every client shared `customer-detail.html`.
*/

const KEY = 'alist-admin:back:';

/** A visited page, as the router reports it: an origin-relative href and its pathname. */
interface Visit {
    pathname: string;
    href: string;
}

/*
  Screens there is no returning to.

  The auth screens are the prototype's "not the login or not-built pages". The create forms are
  ours: a form is gone the moment it is submitted, and the record it made must not point Back at
  a blank one. Every create screen in this app is its own `/new` route by convention, and ids are
  numeric, so no record can be mistaken for one.
*/
const AUTH_SCREENS: readonly string[] = ['/login', '/forgot-password', '/set-password'];

function returnable(pathname: string) {
    return !AUTH_SCREENS.includes(pathname) && !pathname.endsWith('/new');
}

function storage() {
    try {
        return window.sessionStorage;
    } catch {
        // Storage switched off: no memory, and every screen's default still works.
        return undefined;
    }
}

function remembered(pathname: string) {
    try {
        return storage()?.getItem(KEY + pathname) ?? undefined;
    } catch {
        return undefined;
    }
}

function remember(pathname: string, href: string) {
    try {
        storage()?.setItem(KEY + pathname, href);
    } catch {
        // Private mode or over quota: the same, no memory and no harm.
    }
}

/** The pathname of a remembered href, which is what tells a return from a fresh arrival. */
function pathnameOf(href: string) {
    return href.split(/[?#]/)[0];
}

/**
 * Record that `to` was reached from `from`.
 *
 * <p>Called for every resolved navigation, including the ones that must not be remembered, so
 * that the whole rule reads in one place.</p>
 */
export function recordVisit(from: Visit | undefined, to: Visit) {
    // Nothing to come from is a direct link or a bookmark. The same pathname again is a tab, a
    // filter or a sort on the page already open, and none of those is somewhere to go back to.
    if (!from || from.pathname === to.pathname) return;

    const theirs = remembered(from.pathname);
    // Coming BACK from a page is not coming FROM it: when the page just left pointed its own way
    // back at this one, the visitor has returned, and this page keeps the way back it had.
    if (theirs && pathnameOf(theirs) === to.pathname) return;

    // A screen there is no returning to hands on its own way back rather than becoming it, so a
    // product created from a filtered register returns to that register and not to the blank form
    // that made it.
    const target = returnable(from.pathname) ? from.href : theirs;
    if (target) remember(to.pathname, target);
}

/** Where Back goes from `pathname`: where the visitor came from, else the screen's default. */
export function backTargetFor(pathname: string, fallback: string) {
    // Session storage is writable by anything on this origin and the value feeds a navigation, so
    // it is checked the way a post-login redirect is: same-origin paths only.
    return safeRedirect(remembered(pathname), fallback);
}

let here = typeof window === 'undefined' ? '/' : window.location.pathname;
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/**
 * Follow the router for the rest of the session. One subscription, installed beside the router in
 * main.tsx: the rule is application-wide and no screen opts into it.
 *
 * <p>`onResolved` fires before the new page mounts, so the header reads a target that is already
 * current. Listener errors are swallowed by the router, so this can never break a navigation.</p>
 */
export function watchBackTrail(router: AnyRouter) {
    return router.subscribe('onResolved', ({ fromLocation, toLocation }) => {
        recordVisit(fromLocation, toLocation);
        if (here === toLocation.pathname) return;
        here = toLocation.pathname;
        listeners.forEach((listener) => listener());
    });
}

export function useBackTarget(fallback: string): string;
export function useBackTarget(fallback: string | undefined): string | undefined;
/**
 * Where this page's Back goes, given the screen's default.
 *
 * <p>Reads the trail rather than the router, so a header can still be rendered on its own in a
 * test, and so the memory has one reader rather than one per screen.</p>
 */
export function useBackTarget(fallback: string | undefined) {
    const read = () => (fallback === undefined ? undefined : backTargetFor(here, fallback));
    return useSyncExternalStore(subscribe, read, read);
}
